/**
 * ReportesPendientesAlbaranPanel
 * ------------------------------
 * Lista reportes ya cerrados (montado/semi) que aún NO se han enviado a
 * FacturaDirecta como albarán. Sirve para hacer el "limpiado de fin de mes":
 * el admin selecciona varios y los envía en bloque agrupados por cliente.
 *
 * Endpoints:
 *   GET  /albaranes/reportes-pendientes?[cliente_id]&[mes]
 *   POST /albaranes/reportes-pendientes/enviar-fd-batch  { items: [{ruta_id, idx}], nuevo }
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ClipboardList, RefreshCw, Send, AlertTriangle, CheckCircle2,
  Loader2, Filter, Calendar,
} from "lucide-react";
import { Link } from "react-router-dom";
import FDDiagDialog from "@/components/FDDiagDialog";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export default function ReportesPendientesAlbaranPanel({ onChange }) {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroMes, setFiltroMes] = useState("");           // YYYY-MM
  const [seleccion, setSeleccion] = useState(new Set());    // claves "ruta_id|idx"
  const [sending, setSending] = useState(false);
  const [syncingCli, setSyncingCli] = useState(null);
  const [diagCli, setDiagCli] = useState(null);   // { id, nombre } para abrir diag dialog

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/albaranes/reportes-pendientes", {
        params: filtroMes ? { mes: filtroMes } : {},
      });
      setReportes(data.reportes || []);
      setSeleccion(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando pendientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroMes]);

  // Filtrado por texto cliente
  const visibles = useMemo(() => {
    const q = filtroCliente.trim().toLowerCase();
    if (!q) return reportes;
    return reportes.filter((r) =>
      [r.cliente_nombre, r.espacio_nombre, r.peticion_codigo, r.codigo_ruta]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [reportes, filtroCliente]);

  // Agrupar por cliente para mostrar el KPI por bloque
  const porCliente = useMemo(() => {
    const m = new Map();
    for (const r of visibles) {
      const k = r.cliente_id || "sin_cliente";
      if (!m.has(k)) m.set(k, {
        cliente_id: r.cliente_id,
        cliente_nombre: r.cliente_nombre || "(sin cliente)",
        cliente_fd_sync: r.cliente_fd_sync,
        reportes: [],
        total: 0,
      });
      const g = m.get(k);
      g.reportes.push(r);
      g.total += Number(r.precio_total ?? r.precio_montaje ?? 0);
    }
    return Array.from(m.values()).sort((a, b) => b.total - a.total);
  }, [visibles]);

  const keyOf = (r) => `${r.ruta_id}|${r.idx}`;
  const toggle = (r) => {
    const k = keyOf(r);
    const nx = new Set(seleccion);
    nx.has(k) ? nx.delete(k) : nx.add(k);
    setSeleccion(nx);
  };
  const toggleGrupo = (grupo) => {
    const keys = grupo.reportes.map(keyOf);
    const allSel = keys.every((k) => seleccion.has(k));
    const nx = new Set(seleccion);
    keys.forEach((k) => allSel ? nx.delete(k) : nx.add(k));
    setSeleccion(nx);
  };
  const toggleTodos = () => {
    if (seleccion.size === visibles.length) setSeleccion(new Set());
    else setSeleccion(new Set(visibles.map(keyOf)));
  };

  const seleccionados = useMemo(
    () => visibles.filter((r) => seleccion.has(keyOf(r))),
    [visibles, seleccion]
  );
  const totalSeleccionado = seleccionados.reduce((acc, r) => acc + Number(r.precio_total ?? r.precio_montaje ?? 0), 0);
  const algunSinSync = seleccionados.some((r) => !r.cliente_fd_sync);

  const enviarBatch = async () => {
    if (seleccionados.length === 0) { toast.info("Selecciona al menos un reporte"); return; }
    if (algunSinSync) {
      const ok = window.confirm(
        "Algunos clientes seleccionados NO están sincronizados con FacturaDirecta.\n\n" +
        "Esos reportes fallarán. ¿Continuar con los que sí están sincronizados?"
      );
      if (!ok) return;
    }
    if (!window.confirm(
      `Vas a enviar ${seleccionados.length} reportes a FacturaDirecta.\n\n` +
      `Importe total: ${eur(totalSeleccionado)}\n\n` +
      `Se agruparán en el albarán FD abierto del mes para cada cliente. ¿Continuar?`
    )) return;

    setSending(true);
    try {
      const { data } = await api.post("/albaranes/reportes-pendientes/enviar-fd-batch", {
        items: seleccionados.map((r) => ({ ruta_id: r.ruta_id, idx: r.idx })),
        nuevo: false,
      });
      const ok = data.ok || 0;
      const fail = data.fallos || 0;
      if (fail === 0) {
        toast.success(`✅ ${ok} reportes enviados a FD correctamente`, { duration: 6000 });
      } else {
        // Mostrar primer error como detalle
        const primerError = (data.resultados || []).find((r) => !r.ok)?.error || "—";
        toast.warning(`${ok} OK · ${fail} con fallos. Primer error: ${primerError}`, { duration: 9000 });
      }
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando batch a FD");
    } finally {
      setSending(false);
    }
  };

  const syncCliente = async (clienteId, clienteNombre) => {
    setSyncingCli(clienteId);
    try {
      const { data } = await api.post(`/clientes/${clienteId}/sync-fd`);
      if (data.cliente?.fd_id) {
        toast.success(`✓ ${clienteNombre} sincronizado con FD (fd_id=${data.cliente.fd_id.slice(0, 12)}…)`);
      } else {
        toast.warning(`Sync intentada pero ${clienteNombre} sigue sin fd_id. Revisa CIF en FacturaDirecta.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error sincronizando cliente", { duration: 12000 });
    } finally {
      setSyncingCli(null);
    }
  };

  const [syncMasivo, setSyncMasivo] = useState(false);
  const sincronizarTodos = async () => {
    const sinSync = porCliente.filter((g) => !g.cliente_fd_sync && g.cliente_id);
    if (sinSync.length === 0) {
      toast.info("Todos los clientes ya están sincronizados con FD");
      return;
    }
    if (!window.confirm(
      `Se va a intentar sincronizar ${sinSync.length} clientes con FacturaDirecta:\n\n` +
      sinSync.map((g) => `  • ${g.cliente_nombre}`).join("\n") +
      "\n\n¿Continuar?"
    )) return;
    setSyncMasivo(true);
    try {
      const { data } = await api.post("/clientes/sync-fd-masivo");
      const s = data.summary || {};
      const ok = (s.created || 0) + (s.updated || 0);
      const err = s.errors || 0;
      if (err === 0) {
        toast.success(`✅ ${ok} clientes sincronizados con FD (${data.total} en total)`, { duration: 8000 });
      } else {
        const primer = (s.details_errors || [])[0];
        const detalle = primer ? `${primer.nombre}: ${typeof primer.error === "string" ? primer.error : JSON.stringify(primer.error).slice(0, 150)}` : "";
        toast.warning(`${ok} OK · ${err} con error. Primer error: ${detalle}`, { duration: 15000 });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en sync masivo", { duration: 12000 });
    } finally {
      setSyncMasivo(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-zinc-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando pendientes…
      </div>
    );
  }

  const fmtMes = (m) => {
    if (!m) return "Todos";
    const [y, mm] = m.split("-");
    const nombres = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return `${nombres[parseInt(mm) - 1]} ${y}`;
  };

  // Sugerir mes en curso si no hay filtro
  const today = new Date();
  const mesActual = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const mesAnterior = (() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl" data-testid="reportes-pendientes-panel">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-zinc-800 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-300 flex items-center justify-center">
            <ClipboardList className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-100">Reportes pendientes de albaranar</div>
            <div className="text-[11px] text-zinc-500">
              {reportes.length} reporte{reportes.length === 1 ? "" : "s"} · {eur(reportes.reduce((a, r) => a + Number(r.precio_total ?? r.precio_montaje ?? 0), 0))} · selecciona y agrupa por cliente al cierre de mes
            </div>
          </div>
        </div>
        <Button onClick={load} variant="ghost" size="sm"
          data-testid="btn-refresh-pendientes"
          className="h-8 text-xs text-zinc-400 hover:text-amber-300">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar
        </Button>
        <Button onClick={sincronizarTodos} disabled={syncMasivo}
          data-testid="btn-sync-todos-fd"
          variant="outline" size="sm"
          className="h-8 text-xs bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
          {syncMasivo ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
          Sincronizar TODOS los clientes sin FD
        </Button>
      </div>

      {/* Filtros */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3 flex-wrap text-xs">
        <Filter className="w-3.5 h-3.5 text-zinc-500" />
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3 text-zinc-500" />
          <select
            value={filtroMes}
            onChange={(e) => setFiltroMes(e.target.value)}
            data-testid="select-mes"
            className="bg-zinc-950 border border-zinc-800 rounded h-7 px-2 text-zinc-200 text-xs"
          >
            <option value="">Todos los meses</option>
            <option value={mesActual}>{fmtMes(mesActual)} (en curso)</option>
            <option value={mesAnterior}>{fmtMes(mesAnterior)}</option>
            <option value="2026-04">Abr 2026</option>
            <option value="2026-03">Mar 2026</option>
          </select>
        </div>
        <Input
          placeholder="Buscar cliente, espacio, código…"
          value={filtroCliente}
          onChange={(e) => setFiltroCliente(e.target.value)}
          data-testid="input-buscar-cliente"
          className="h-7 max-w-xs text-xs bg-zinc-950 border-zinc-800"
        />
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer text-zinc-300"
            data-testid="checkbox-todos-label">
            <input type="checkbox"
              checked={visibles.length > 0 && seleccion.size === visibles.length}
              onChange={toggleTodos}
              data-testid="checkbox-todos"
              className="accent-amber-500 w-3.5 h-3.5" />
            Todos ({visibles.length})
          </label>
          <Button
            onClick={enviarBatch}
            disabled={sending || seleccion.size === 0}
            data-testid="btn-batch-enviar-fd"
            className="h-8 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-xs"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Albaranar {seleccion.size > 0 ? `${seleccion.size} · ${eur(totalSeleccionado)}` : "selección"}
          </Button>
        </div>
      </div>

      {/* Lista agrupada por cliente */}
      <div className="divide-y divide-zinc-800">
        {visibles.length === 0 ? (
          <div className="text-center py-14 text-zinc-500 text-sm">
            <CheckCircle2 className="w-8 h-8 text-emerald-500/40 mx-auto mb-2" />
            No hay reportes pendientes de albaranar
            {filtroMes && <span className="block text-[11px] mt-1">en {fmtMes(filtroMes)}</span>}
          </div>
        ) : porCliente.map((grupo) => {
          const todosSel = grupo.reportes.every((r) => seleccion.has(keyOf(r)));
          return (
            <div key={grupo.cliente_id || "sin"} className="" data-testid={`grupo-cli-${grupo.cliente_id}`}>
              {/* Cabecera de grupo (cliente) */}
              <div className="bg-zinc-950/40 px-4 py-2 flex items-center gap-3 flex-wrap">
                <input type="checkbox" checked={todosSel} onChange={() => toggleGrupo(grupo)}
                  data-testid={`grupo-check-${grupo.cliente_id}`}
                  className="accent-amber-500 w-3.5 h-3.5" />
                <div className="text-sm font-semibold text-zinc-100 flex-1 min-w-0 truncate">
                  {grupo.cliente_nombre}
                </div>
                {!grupo.cliente_fd_sync && grupo.cliente_id && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setDiagCli({ id: grupo.cliente_id, nombre: grupo.cliente_nombre })}
                    data-testid={`btn-sync-cli-${grupo.cliente_id}`}
                    className="h-6 text-[10px] bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Sincronizar con FD
                  </Button>
                )}
                {grupo.cliente_fd_sync && (
                  <span className="text-[10px] text-emerald-300 inline-flex items-center gap-0.5">
                    <CheckCircle2 className="w-3 h-3" /> Sync FD
                  </span>
                )}
                <div className="text-xs text-zinc-400">
                  {grupo.reportes.length} reporte{grupo.reportes.length === 1 ? "" : "s"}
                </div>
                <div className="text-sm font-bold text-amber-300 min-w-[80px] text-right">
                  {eur(grupo.total)}
                </div>
              </div>
              {/* Reportes del cliente */}
              <ul>
                {grupo.reportes.map((r) => {
                  const isSel = seleccion.has(keyOf(r));
                  return (
                    <li key={keyOf(r)}
                      onClick={() => toggle(r)}
                      data-testid={`reporte-${r.ruta_id}-${r.idx}`}
                      className={`px-4 py-2 flex items-center gap-3 cursor-pointer text-xs transition-colors flex-wrap ${
                        isSel ? "bg-amber-500/5" : "hover:bg-zinc-800/30"
                      }`}>
                      <input type="checkbox" checked={isSel} readOnly
                        className="accent-amber-500 w-3.5 h-3.5 shrink-0" />
                      <span className="font-mono text-[10px] text-amber-300/80 shrink-0 w-32 truncate">
                        {r.codigo_ruta}#{r.idx + 1}
                      </span>
                      <span className="text-zinc-200 truncate flex-1 min-w-[180px]">
                        {r.espacio_nombre || "—"}
                        {r.espacio_ciudad && <span className="text-zinc-500"> · {r.espacio_ciudad}</span>}
                      </span>
                      <span className="text-zinc-500 text-[10px] shrink-0">{r.fecha}</span>
                      {r.peticion_codigo && (
                        <Link to={`/peticiones/${r.peticion_id}`} onClick={(e) => e.stopPropagation()}
                          className="text-cyan-300 hover:text-cyan-200 font-mono text-[10px] shrink-0">
                          {r.peticion_codigo}
                        </Link>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${
                        r.estado_cierre === "montado"
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                      }`}>
                        {r.estado_cierre || "—"}
                      </span>
                      <span className="font-mono text-zinc-100 font-semibold min-w-[80px] text-right shrink-0"
                        title={r.precio_produccion ? `Montaje ${eur(r.precio_montaje)} + Producción ${eur(r.precio_produccion)}` : ""}>
                        {eur(r.precio_total ?? r.precio_montaje)}
                        {r.precio_produccion > 0 && (
                          <div className="text-[9px] text-zinc-500 font-normal">
                            M {eur(r.precio_montaje)} · P {eur(r.precio_produccion)}
                          </div>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Dialog de diagnóstico FD */}
      <FDDiagDialog
        open={!!diagCli}
        onOpenChange={(o) => { if (!o) setDiagCli(null); }}
        clienteId={diagCli?.id}
        clienteNombre={diagCli?.nombre}
        onSyncOk={() => { setDiagCli(null); load(); }}
      />
    </div>
  );
}
